import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../hooks/useCompany';
import { customerAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Users,
  Plus,
  Search,
  Phone,
  Mail,
  CreditCard,
  Eye,
  DollarSign,
  Wallet,
} from 'lucide-react';

const formatCurrency = (value, currency = 'USD') => {
  if (currency === 'LRD') {
    return `L$${new Intl.NumberFormat('en-US').format(value || 0)}`;
  }
  return `$${new Intl.NumberFormat('en-US').format(value || 0)}`;
};

function CreateCustomerModal({ isOpen, onClose, onSuccess, companyId }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [idType, setIdType] = useState('');
  const [idNumber, setIdNumber] = useState('');

  const mutation = useMutation({
    mutationFn: (data) => customerAPI.create(data),
    onSuccess: () => {
      toast.success('Customer created successfully');
      onSuccess();
      onClose();
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    },
  });

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setPhone('');
    setEmail('');
    setAddress('');
    setIdType('');
    setIdNumber('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!firstName || !lastName || !phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    mutation.mutate({
      company_id: companyId,
      first_name: firstName,
      last_name: lastName,
      phone,
      email: email || undefined,
      address: address || undefined,
      id_type: idType || undefined,
      id_number: idNumber || undefined,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Create Customer</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">First Name *</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                  data-testid="input-first-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last Name *</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                  data-testid="input-last-name"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone Number *</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+231 770 000 000"
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
                data-testid="input-phone"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john.doe@example.com"
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
                data-testid="input-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Address</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St, Monrovia"
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
                data-testid="input-address"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">ID Type</label>
                <select
                  value={idType}
                  onChange={(e) => setIdType(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                  data-testid="select-id-type"
                >
                  <option value="">Select ID Type</option>
                  <option value="national_id">National ID</option>
                  <option value="passport">Passport</option>
                  <option value="driver_license">Driver's License</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">ID Number</label>
                <input
                  type="text"
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  placeholder="ID Number"
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                  data-testid="input-id-number"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-border rounded-md hover:bg-muted">
                Cancel
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                data-testid="button-create-customer"
              >
                {mutation.isPending ? 'Creating...' : 'Create Customer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function Customers() {
  const { selectedCompany, getDFSCompany } = useCompany();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const dfsCompany = getDFSCompany ? getDFSCompany() : selectedCompany;
  const companyId = dfsCompany?.id;

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ['customers', companyId, searchQuery],
    queryFn: () => customerAPI.getAll(companyId, searchQuery).then((res) => res.data),
    enabled: !!companyId,
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries(['customers', companyId]);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const activeCustomers = customers.filter((c) => c.is_active);
  const customersWithLoans = customers.filter((c) => c.active_loans > 0);

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Customers</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage DFS customer accounts
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          data-testid="button-add-customer"
        >
          <Plus className="h-4 w-4" />
          Add Customer
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Total Customers</span>
          </div>
          <p className="text-2xl font-semibold">{customers.length}</p>
          <p className="text-xs text-muted-foreground">{activeCustomers.length} active</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">With Active Loans</span>
          </div>
          <p className="text-2xl font-semibold">{customersWithLoans.length}</p>
          <p className="text-xs text-muted-foreground">customers</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-4 w-4 text-green-600" />
            <span className="text-sm text-muted-foreground">Total Balances</span>
          </div>
          <p className="text-lg font-semibold text-green-600">
            {formatCurrency(customers.reduce((sum, c) => sum + (c.usd_balance || 0), 0), 'USD')}
          </p>
          <p className="text-xs text-blue-600">
            {formatCurrency(customers.reduce((sum, c) => sum + (c.lrd_balance || 0), 0), 'LRD')}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, phone, or customer number..."
          className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md"
          data-testid="input-search"
        />
      </div>

      {/* Customers Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left py-3 px-4 font-medium">Customer</th>
                <th className="text-left py-3 px-4 font-medium">Contact</th>
                <th className="text-right py-3 px-4 font-medium">USD Balance</th>
                <th className="text-right py-3 px-4 font-medium">LRD Balance</th>
                <th className="text-center py-3 px-4 font-medium">Loans</th>
                <th className="text-center py-3 px-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr key={customer.id} className="border-t border-border/50 hover:bg-muted/30">
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium">{customer.first_name} {customer.last_name}</p>
                      <p className="text-xs text-muted-foreground">{customer.customer_number}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Phone className="h-3 w-3" />
                        <span className="text-xs">{customer.phone}</span>
                      </div>
                      {customer.email && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Mail className="h-3 w-3" />
                          <span className="text-xs">{customer.email}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-green-600">
                    {formatCurrency(customer.usd_balance, 'USD')}
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-blue-600">
                    {formatCurrency(customer.lrd_balance, 'LRD')}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {customer.active_loans > 0 ? (
                      <span className="px-2 py-1 bg-yellow-500/10 text-yellow-600 rounded-full text-xs">
                        {customer.active_loans} active
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">
                        {customer.total_loans} total
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <Link
                      to={`/dfs/loans?customer_id=${customer.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-primary hover:bg-primary/10 rounded"
                      data-testid={`button-view-${customer.id}`}
                    >
                      <Eye className="h-3 w-3" />
                      View
                    </Link>
                  </td>
                </tr>
              ))}
              {customers.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-muted-foreground">
                    {searchQuery ? 'No customers found matching your search' : 'No customers yet. Click "Add Customer" to create one.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      <CreateCustomerModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleRefresh}
        companyId={companyId}
      />
    </div>
  );
}
